import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import axios from "axios";
import { Modal } from "./UI/Modal";
import { Button } from "./UI/Button";
import { Icon } from "./UI/Icon";

type Area = { x: number; y: number; width: number; height: number };

const WORKER_URL = "https://nas.sendsierra.com";

const ImageUploader = ({
 pictureUrl,
 setField,
}: {
  pictureUrl: any;
  setField: any;
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const onDrop = (acceptedFiles: File[]) => {
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setIsModalOpen(true);
    };
    reader.readAsDataURL(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleUpload = async () => {
    try {
      if (!imageSrc || !croppedAreaPixels) return;
      setIsUploading(true);

      const file = await getCroppedImage(imageSrc, croppedAreaPixels);

      const form = new FormData();
      form.append("file", file);

      const { data } = await axios.post(`${WORKER_URL}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setField(data.url);

      setImageSrc(null);
      setIsModalOpen(false);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };


  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!pictureUrl) return;

    try {
      setIsRemoving(true);

      const urlParts = pictureUrl.split("/");
      const fileName = decodeURIComponent(urlParts[urlParts.length - 1]);

      await axios.post(
        `${WORKER_URL}/delete-file`,
        { fileName },
        { headers: { "Content-Type": "application/json" } }
      );

      setField("");
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="flex flex-col mt-1">
      <label className="text-sm text-slate-600">Profile Photo</label>

      <div
        {...getRootProps()}
        className="flex items-center justify-center px-4 h-48 border-2 border-dashed text-center cursor-pointer rounded-lg hover:bg-slate-100 active:bg-slate-100"
      >
        {!pictureUrl ? (
          <div className="flex flex-col justify-center">
            <input {...getInputProps()} />
            <span className="text-slate-500">
              <Icon prefix="fa-solid" icon="upload" size="xl" />
            </span>
            <p className="mt-4 text-xs font-medium text-slate-500 uppercase">
              Drag & drop an image here,
              <br /> or click to select
            </p>
          </div>
        ) : (
          <div className="flex relative">
            <img src={pictureUrl} alt="" className="rounded-lg" />
            <button
              type="button"
              onClick={handleRemove}
              disabled={isRemoving}
              className={`absolute top-0 right-0 bg-red-500 text-white rounded-bl-lg rounded-tr-lg py-0.5 px-2 ${
                isRemoving ? "cursor-not-allowed" : ""
              }`}
            >
              {isRemoving ? (
                <Icon prefix="fa-solid" icon="spinner" size="sm" spin />
              ) : (
                <Icon prefix="fa-solid" icon="trash-can" size="xs" />
              )}
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} setIsOpen={() => setIsModalOpen(false)}>
        {imageSrc && (
          <div className="relative w-full h-[400px] bg-black">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <div className="flex items-center w-1/2">
            <div className="flex items-center space-x-2 border border-slate-200 rounded-lg py-1 px-2">
              <span className="text-blue-500">
                <Icon prefix="fa-solid" icon="info-square" size="2xl" />
              </span>
              <p className="text-xs text-slate-700">
                Use the mouse wheel to zoom, drag with the left mouse button to
                move the crop area.
              </p>
            </div>
          </div>

          <div className="flex w-1/2 gap-x-2 justify-end">
            <Button color="white" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button color="blue" onClick={handleUpload} isLoading={isUploading}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ImageUploader;

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImage = async (src: string, crop: Area): Promise<File> => {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  const targetSize = 120;
  canvas.width = targetSize;
  canvas.height = targetSize;

  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    targetSize,
    targetSize
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) throw new Error("Crop failed");
      resolve(new File([blob], `${Date.now()}.jpg`, { type: "image/jpeg" }));
    }, "image/jpeg", 0.8);
  });
};
